import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Home from "@/pages/Home";
import CardSite from "@/pages/CardSite";
import PwaWallet from "@/pages/PwaWallet";
import UserEditCard from "@/pages/UserEditCard";
import NotFound from "@/pages/NotFound";
import InvitePage from "@/pages/InvitePage";

// Admin pages
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import CardsPage from "@/pages/admin/CardsPage";
import CardEditPage from "@/pages/admin/CardEditPage";
import ConfigPage from "@/pages/admin/ConfigPage";
import VariablesPage from "@/pages/admin/VariablesPage";
import MediaPage from "@/pages/admin/MediaPage";
import FontsPage from "@/pages/admin/FontsPage";
import FormsPage from "@/pages/admin/FormsPage";
import RedemptionsPage from "@/pages/admin/RedemptionsPage";
import UsersPage from "@/pages/admin/UsersPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";

// User dashboard pages
import MyCardsPage from "@/pages/dashboard/MyCardsPage";

// Calendar pages
import CalendarDashboard from "@/pages/calendar/CalendarDashboard";
import CalendarAccounts from "@/pages/calendar/CalendarAccounts";
import CalendarEvents from "@/pages/calendar/CalendarEvents";
import ShareLinksPage from "@/pages/calendar/ShareLinksPage";
import BookingPagesPage from "@/pages/calendar/BookingPagesPage";
import ApiKeysPage from "@/pages/calendar/ApiKeysPage";

// Public booking
import PublicBooking from "@/pages/PublicBooking";

function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
        <a href="/" className="text-primary hover:underline">Back to Home</a>
      </div>
    );
  }

  return <>{children}</>;
}

/** Redirect authenticated users to their appropriate dashboard */
function AuthRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Home />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/u/:personalId" element={<CardSite />} />
      <Route path="/pwa/:hash" element={<PwaWallet />} />
      <Route path="/book/:pageId" element={<PublicBooking />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* User self-edit */}
      <Route path="/edit" element={
        <ProtectedRoute><UserEditCard /></ProtectedRoute>
      } />

      {/* Admin routes (admin only) */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}><AdminLayout section="admin" /></ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="cards" element={<CardsPage />} />
        <Route path="cards/new" element={<CardEditPage />} />
        <Route path="cards/:id" element={<CardEditPage />} />
        <Route path="config" element={<ConfigPage />} />
        <Route path="variables" element={<VariablesPage />} />
        <Route path="media" element={<MediaPage />} />
        <Route path="fonts" element={<FontsPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="redemptions" element={<RedemptionsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
      </Route>

      {/* User/Secretary dashboard (user + secretary) */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={["user", "secretary"]}><AdminLayout section="dashboard" /></ProtectedRoute>
      }>
        <Route index element={<MyCardsPage />} />
        <Route path="cards/new" element={<CardEditPage />} />
        <Route path="cards/:id" element={<CardEditPage />} />
        <Route path="calendar" element={<CalendarDashboard />} />
        <Route path="events" element={<CalendarEvents />} />
        <Route path="share-links" element={<ShareLinksPage />} />
        <Route path="booking-pages" element={<BookingPagesPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
      </Route>

      {/* Calendar/SyncBridge routes (admin read-only, user/secretary manage) */}
      <Route path="/calendar" element={
        <ProtectedRoute><AdminLayout section="calendar" /></ProtectedRoute>
      }>
        <Route index element={<CalendarDashboard />} />
        <Route path="accounts" element={<CalendarAccounts />} />
        <Route path="events" element={<CalendarEvents />} />
        <Route path="share-links" element={<ShareLinksPage />} />
        <Route path="booking-pages" element={<BookingPagesPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
